import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { financeApi } from '../../services/financeApi';
import { FinanceExportType, FinancePeriodEntry, VatReportResponse } from '../../types/finance';
import Money from '../../components/finance/Money';
import InfoTip from '../../components/finance/InfoTip';
import PeriodPicker, { currentDubaiPeriod } from '../../components/finance/PeriodPicker';
import ProviderVatReconcile from '../../components/finance/ProviderVatReconcile';
import { GlossaryKey } from '../../components/finance/glossary';

const EXPORTS: { type: FinanceExportType; label: string }[] = [
  { type: 'vat-summary', label: 'VAT summary' },
  { type: 'vat-detail', label: 'VAT detail' },
  { type: 'fees', label: 'Fees' },
  { type: 'refunds', label: 'Refunds' },
  { type: 'journals', label: 'Journals (GL)' },
];

/** Percentages are null when their base (GMV / commission) is 0 — the backend's `pct()` — so render an em dash. */
const pct = (v: number | null | undefined) =>
  v == null ? '—' : `${v.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

/** A labelled figure line inside a VAT box. The ⓘ travels with the value (Money). */
function Line({ label, children, emphasis = false }: { label: string; children: ReactNode; emphasis?: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem',
      padding: '0.375rem 0', fontSize: emphasis ? '1rem' : '0.875rem', fontWeight: emphasis ? 700 : 400,
      borderTop: emphasis ? '1px solid var(--admin-border)' : undefined, marginTop: emphasis ? '0.25rem' : undefined,
    }}>
      <span style={{ color: emphasis ? 'var(--admin-text)' : 'var(--admin-text-muted)' }}>{label}</span>
      {children}
    </div>
  );
}

/** KPI card: title carries the ⓘ, the main figure is a Money without a second icon. */
function Kpi({ title, glossaryKey, children, sub }: { title: string; glossaryKey: GlossaryKey; children: ReactNode; sub?: ReactNode }) {
  return (
    <div className="admin-card" style={{ padding: '1rem' }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', display: 'flex', alignItems: 'center' }}>
        {title}
        <InfoTip glossaryKey={glossaryKey} />
      </div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, marginTop: '0.25rem' }}>{children}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>{sub}</div>}
    </div>
  );
}

/**
 * Spec 053 Flow C — month-end VAT and finance pack (AC-12, AC-14, AC-15, AC-16, AC-16b).
 * One fetch per period change; every figure goes through <Money glossaryKey> (BR-31).
 *
 * The backend degrades rather than fails: `box9` / `netPayable` / `refunds` / `feeLeak` are null when
 * billing is unreachable and `warnings` says so — Box 1 (own documents) always renders, the rest
 * shows em dashes and the warning banner.
 */
export default function ReportsTab() {
  const auth = useAuth();
  const [period, setPeriod] = useState(currentDubaiPeriod());
  const [report, setReport] = useState<VatReportResponse | null>(null);
  const [entries, setEntries] = useState<FinancePeriodEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<FinanceExportType | null>(null);
  const [exportNote, setExportNote] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!auth.token) return;
    setLoading(true);
    setError(null);
    try {
      const [r, e] = await Promise.all([
        financeApi.getVatReport(period, auth.token),
        financeApi.listPeriodEntries(period, auth.token).catch(() => [] as FinancePeriodEntry[]),
      ]);
      setReport(r);
      setEntries(e);
    } catch (err) {
      setReport(null);
      setError(err instanceof Error ? err.message : 'Failed to load the report');
    } finally {
      setLoading(false);
    }
  }, [auth.token, period]);

  useEffect(() => { load(); }, [load]);

  const runExport = async (type: FinanceExportType) => {
    if (!auth.token || !report) return;
    setExporting(type);
    setExportNote(null);
    setExportError(null);
    try {
      const filename = await financeApi.downloadExport(type, period, auth.token);
      setExportNote(`Downloaded ${filename}`);
    } catch (err) {
      // 413 "Export too large — narrow the period" / 503 billing unavailable — the backend's message is the guidance.
      setExportError(err instanceof Error ? `Export failed: ${err.message}` : 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
        <PeriodPicker value={period} onChange={setPeriod} disabled={loading} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>Export CSV:</span>
          {EXPORTS.map((x) => (
            <button
              key={x.type}
              type="button"
              className="admin-btn admin-btn-sm admin-btn-outline"
              disabled={!report || exporting !== null}
              onClick={() => runExport(x.type)}
            >
              {exporting === x.type ? 'Exporting…' : x.label}
            </button>
          ))}
        </div>
      </div>
      {exportNote && <div className="admin-info-banner info" role="status">{exportNote}</div>}
      {exportError && <div className="admin-info-banner danger" role="alert" data-testid="export-error">{exportError}</div>}

      {error && <div className="admin-info-banner danger" role="alert">{error}</div>}

      {loading && !report ? (
        <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
      ) : report && (
        <>
          {report.provisional && (
            <div className="admin-info-banner warning" role="status" data-testid="provisional-banner">
              <strong>Provisional — period not closed.</strong> Today falls inside {report.period}; figures can still move and
              exports are suffixed <code>-provisional</code>.
            </div>
          )}
          {report.warnings.length > 0 && (
            <div className="admin-info-banner warning" role="status" data-testid="report-warnings">
              <strong>Some figures are unavailable.</strong>
              <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.25rem' }}>
                {report.warnings.map((w) => <li key={w}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* VAT boxes */}
          <div className="admin-grid-3">
            <div className="admin-card">
              <div className="admin-card-header">
                <h3 className="admin-card-title">Box 1 — Output VAT</h3>
              </div>
              <Line label="Commission invoiced (net)"><Money amount={report.box1.commissionNet} glossaryKey="commissionNet" /></Line>
              <Line label="VAT on commission"><Money amount={report.box1.commissionVat} glossaryKey="commissionVat" /></Line>
              <Line label="Commission credited (net)"><Money amount={-report.box1.creditedNet} glossaryKey="creditedNet" /></Line>
              <Line label="VAT credited"><Money amount={-report.box1.creditedVat} glossaryKey="creditedVat" /></Line>
              <Line label="Delivery fees invoiced (net)"><Money amount={report.box1.deliveryNet} glossaryKey="deliveryNet" /></Line>
              <Line label="VAT on delivery fees"><Money amount={report.box1.deliveryVat} glossaryKey="deliveryVat" /></Line>
              {/* Spec 068 V13 — always listed: a report shows its zeros. */}
              <Line label="Service fees invoiced (net)"><Money amount={report.box1.serviceFeeNet ?? 0} glossaryKey="serviceFee" /></Line>
              <Line label="VAT on service fees"><Money amount={report.box1.serviceFeeVat ?? 0} glossaryKey="serviceFee" /></Line>
              <Line label="Output VAT" emphasis><Money amount={report.box1.outputVat} glossaryKey="outputVat" /></Line>
            </div>

            <div className="admin-card">
              <div className="admin-card-header">
                <h3 className="admin-card-title">Box 9 — Input VAT</h3>
              </div>
              <Line label="Stripe fees (net)"><Money amount={report.box9?.stripeFeeNet} glossaryKey="stripeFeeNet" /></Line>
              <Line label="Stripe fee VAT"><Money amount={report.box9?.stripeFeeTax} glossaryKey="stripeFeeTax" /></Line>
              <Line label="Paymob input VAT"><Money amount={report.box9?.paymobInputVat} glossaryKey="paymobInputVat" /></Line>
              <Line label="Recovery trip input VAT"><Money amount={report.box9?.tripInputVat} glossaryKey="tripInputVat" /></Line>
              {/* #594 — the AC-43 blank-cost provider's whole invoice VAT arrives in this term and in
                  no other, so leaving the line off makes the emphasised total larger than anything
                  the advisor can see beside it (the #583/#584 defect class). */}
              <Line label="Recovery provider invoice input VAT"><Money amount={report.box9?.providerInvoiceInputVat} glossaryKey="providerInvoiceInputVat" /></Line>
              <Line label="Fee VAT reversed (customer bore it)"><Money amount={report.box9?.feeVatRecovered} glossaryKey="feeVatRecovered" /></Line>
              <Line label="Input VAT" emphasis><Money amount={report.box9?.inputVat} glossaryKey="inputVat" /></Line>
              <div style={{
                marginTop: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.75rem',
                background: (report.box9?.estimatedCount ?? 0) > 0 ? '#fffbeb' : 'var(--admin-bg)', color: 'var(--admin-text-muted)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                  <span>Input VAT not yet evidenced</span>
                  <Money amount={report.box9?.estimatedCount} glossaryKey="estimatedFees" format="count" />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                  <span>Estimated fees (excluded from Box 9)</span>
                  <Money amount={report.box9?.estimatedFeeAmount} glossaryKey="estimatedFees" hideTip />
                </div>
              </div>
            </div>

            <div className="admin-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
              <div style={{ fontSize: '0.8125rem', color: 'var(--admin-text-muted)' }}>Net VAT payable</div>
              <Money
                amount={report.netPayable}
                glossaryKey="netVatPayable"
                style={{ fontSize: '1.75rem', fontWeight: 700, color: (report.netPayable ?? 0) < 0 ? 'var(--admin-success)' : 'var(--admin-text)' }}
              />
              <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>Box 1 − Box 9</div>
            </div>
          </div>

          {/* KPI row */}
          <div className="admin-grid-4" style={{ marginTop: '1rem' }}>
            <Kpi title="GMV" glossaryKey="gmv">
              <Money amount={report.gmv} glossaryKey="gmv" hideTip />
            </Kpi>
            <Kpi title="Commission revenue" glossaryKey="commissionRevenue">
              <Money amount={report.commissionRevenue} glossaryKey="commissionRevenue" hideTip />
            </Kpi>
            <Kpi
              title="Refunds"
              glossaryKey="refundsAmount"
              sub={<><Money amount={report.refunds?.count} glossaryKey="refundsCount" format="count" /> refunds</>}
            >
              <Money amount={report.refunds?.amount} glossaryKey="refundsAmount" hideTip />
            </Kpi>
            <Kpi title="Fee leak" glossaryKey="feeLeak" sub={`${pct(report.feeLeak?.pctOfGmv)} of GMV`}>
              <Money amount={report.feeLeak?.amount} glossaryKey="feeLeak" hideTip />
            </Kpi>
            <Kpi
              title="Goodwill"
              glossaryKey="goodwill"
              sub={`${pct(report.goodwill.pctOfGmv)} of GMV · ${pct(report.goodwill.pctOfCommission)} of commission`}
            >
              <Money amount={report.goodwill.amount} glossaryKey="goodwill" hideTip />
            </Kpi>
            <Kpi
              title="Clawbacks over 60 days"
              glossaryKey="clawbacksOver60"
              sub={<><Money amount={report.clawbacksOver60.count} glossaryKey="clawbacksOver60" format="count" hideTip /> open · PROVISION</>}
            >
              <Money amount={report.clawbacksOver60.amount} glossaryKey="clawbacksOver60" hideTip />
            </Kpi>
            <Kpi title="Credit-note breaches" glossaryKey="creditNoteBreaches" sub="≥ 14 days unissued">
              <Money
                amount={report.creditNoteBreaches.length}
                glossaryKey="creditNoteBreaches"
                format="count"
                hideTip
                style={{ color: report.creditNoteBreaches.length > 0 ? 'var(--admin-danger)' : undefined }}
              />
            </Kpi>
          </div>

          {report.creditNoteBreaches.length > 0 && (
            <div className="admin-card" style={{ marginTop: '1rem' }}>
              <div className="admin-card-header">
                <h3 className="admin-card-title">Credit-note SLA breaches</h3>
                <span className="admin-badge rejected">{report.creditNoteBreaches.length}</span>
              </div>
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Order</th>
                      <th>Refund</th>
                      <th>Refunded</th>
                      <th style={{ textAlign: 'right' }}>Days elapsed</th>
                      <th>Hold reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.creditNoteBreaches.map((b) => (
                      <tr key={`${b.refundId}-${b.source ?? ''}`}>
                        <td>{b.source ?? '—'}</td>
                        <td>{b.orderNumber ?? b.orderId ?? '—'}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{b.refundId}</td>
                        <td>{b.refundedAt ? new Date(b.refundedAt).toLocaleDateString() : '—'}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{b.ageDays}</td>
                        <td>{b.holdReason ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <ProviderVatReconcile
            period={report.period}
            reconciliation={report.providerVatReconciliation}
            paymobInputVat={report.box9?.paymobInputVat ?? 0}
            entries={entries}
            token={auth.token ?? ''}
            onSaved={load}
          />
        </>
      )}
    </div>
  );
}
